import { useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { motion, type PanInfo } from "framer-motion";
import { useAuth } from "../lib/auth";
import { useCompany, useBrandName } from "../lib/company";
import { getInitData, getStartParam, hapticFeedback } from "../lib/telegram";
import { CREATE_TELEGRAM_ACCOUNT } from "../graphql/auth";
import { MintSequence } from "../components/MintSequence";

const introSlides = [
  {
    title: ["Crypto in.", "Card out."],
    subtitle: "Fund a real USD Visa card from BTC, ETH, or USDC in seconds.",
  },
  {
    title: ["It becomes", "dollars."],
    subtitle: "Deposits convert at confirmation and load straight onto your card.",
  },
  {
    title: ["Spend", "anywhere."],
    subtitle: "Anywhere Visa is accepted — with instant card controls in Telegram.",
  },
];

/** Static poster card — the dark object on the light stage */
const PosterCard = ({ brandName, slide }: { brandName: string; slide: number }) => (
  <div className="card-shimmer rounded-[14px] bg-gradient-to-br from-[#12312C] via-card-obj to-[#071512] text-[#E8F5F2] shadow-[0_18px_40px_rgba(11,31,28,0.35)] p-4 aspect-[1.7/1] w-64 flex flex-col justify-between overflow-hidden rotate-[-4deg]">
    <div className="flex justify-between items-start">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#9DBDB6]">Virtual</p>
        <p className="text-sm font-semibold">{brandName} Card</p>
      </div>
      <span className="text-sm italic font-bold">VISA</span>
    </div>
    {slide === 1 ? (
      <div className="font-mono text-[11px] text-[#9DBDB6] space-y-1">
        <div className="flex justify-between"><span>0.0182 ETH</span><span>→ $1,321.80</span></div>
        <div className="flex justify-between border-t border-[#9DBDB6]/30 pt-1 text-[#E8F5F2]">
          <span>LOADED</span><span>$1,240.50</span>
        </div>
      </div>
    ) : (
      <p className="font-mono text-[15px] tracking-[0.14em] text-[#9DBDB6]/70">
        •••• •••• •••• ••••
      </p>
    )}
    <div className="flex justify-between items-end">
      <p className="font-mono text-[10px] uppercase tracking-[0.12em] text-[#9DBDB6]">
        {brandName.toUpperCase()}
      </p>
      {slide === 2 ? (
        <span className="stamp text-mint">Active</span>
      ) : (
        <p className="font-mono text-[10px] text-[#9DBDB6]">EXP ••/••</p>
      )}
    </div>
  </div>
);

export const Onboarding = () => {
  const { setAuthenticated } = useAuth();
  const { company } = useCompany();
  const brandName = useBrandName();
  const companyId = company?.id ?? "";
  const [step, setStep] = useState<"intro" | "auth" | "mint">("intro");
  const [slideIndex, setSlideIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const pendingAuth = useRef<{ token: string; user: any } | null>(null);

  const [createAccount] = useMutation(CREATE_TELEGRAM_ACCOUNT);

  const handleCreateAccount = async () => {
    setError(null);
    setLoading(true);
    try {
      const initData = getInitData();
      const startParam = getStartParam();
      const referralCode = startParam?.startsWith("ref_") ? startParam.slice(4) : startParam || undefined;
      const result = await createAccount({
        variables: { input: { initData, referralCode, companyId } },
      });
      const data = result.data?.createTelegramAccount;
      if (data?.success && data.token && data.user) {
        // Hold auth until the mint completes — the user watches their card
        // get made, then lands signed in.
        pendingAuth.current = { token: data.token, user: data.user };
        setStep("mint");
      } else {
        hapticFeedback.error();
        setError(data?.error || "Something went wrong. Please try again.");
      }
    } catch (err: any) {
      hapticFeedback.error();
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSlideDragEnd = (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const threshold = 50;
    if (info.offset.x < -threshold && slideIndex < introSlides.length - 1) {
      setSlideIndex((i) => i + 1);
      hapticFeedback.selectionChanged();
    } else if (info.offset.x > threshold && slideIndex > 0) {
      setSlideIndex((i) => i - 1);
      hapticFeedback.selectionChanged();
    }
  };

  const handleNextSlide = () => {
    hapticFeedback.light();
    if (slideIndex < introSlides.length - 1) {
      setSlideIndex((i) => i + 1);
    } else {
      setStep("auth");
    }
  };

  if (step === "mint") {
    return (
      <MintSequence
        brandName={brandName}
        caption="your card is being prepared"
        stampText="Minted"
        onComplete={() => {
          const auth = pendingAuth.current;
          if (auth) setAuthenticated(auth.token, auth.user);
        }}
      />
    );
  }

  if (step === "intro") {
    const isLast = slideIndex === introSlides.length - 1;
    return (
      <div className="min-h-screen bg-ground flex flex-col">
        <div className="pt-6 px-6">
          <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
            {brandName}
          </p>
        </div>

        {/* Poster: card up top, headline low + left */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <motion.div
            className="flex flex-1"
            animate={{ x: -slideIndex * 100 + "%" }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            onDragEnd={handleSlideDragEnd}
          >
            {introSlides.map((s, i) => (
              <div key={i} className="min-w-full px-6 flex flex-col">
                <div className="flex-1 flex items-center justify-center py-6">
                  <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                  >
                    <PosterCard brandName={brandName} slide={i} />
                  </motion.div>
                </div>
                <div className="pb-2">
                  <h2 className="font-display font-bold text-[34px] leading-[1.02] tracking-[-0.03em] text-ink">
                    {s.title[0]}
                    <br />
                    {s.title[1]}
                  </h2>
                  <p className="text-muted text-sm mt-2.5 max-w-[30ch] leading-relaxed">
                    {s.subtitle}
                  </p>
                </div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="px-6 pb-8 safe-bottom">
          {/* Perforation progress */}
          <div className="flex gap-1.5 mb-6">
            {introSlides.map((_, i) => (
              <div
                key={i}
                className={`h-1 w-6 rounded-full transition-all duration-300 ${
                  i === slideIndex ? "bg-accent" : "bg-hairline"
                }`}
              />
            ))}
          </div>
          <button
            onClick={handleNextSlide}
            className="w-full h-[52px] bg-ink text-ground rounded-xl font-semibold text-base active:scale-[0.99] transition-transform"
          >
            {isLast ? "Create my card" : "Next"}
          </button>
          {!isLast && (
            <button
              onClick={() => {
                hapticFeedback.light();
                setStep("auth");
              }}
              className="w-full py-3 text-muted text-sm font-medium mt-1"
            >
              Skip
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ground flex flex-col">
      <div className="pt-6 px-6 border-b border-hairline pb-5">
        <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-muted">
          {brandName} · Mint order
        </p>
      </div>

      <div className="flex-1 flex flex-col justify-end px-6 pb-2">
        <h1 className="font-display font-bold text-[34px] leading-[1.02] tracking-[-0.03em] text-ink">
          Get your
          <br />
          card.
        </h1>
        <p className="text-muted text-sm mt-2.5 max-w-[32ch] leading-relaxed">
          One tap. Your Telegram account becomes a {brandName} account, and your card
          goes to the press.
        </p>
      </div>

      <div className="px-6 pb-8 safe-bottom">
        {error && (
          <div className="border-l-[3px] border-error bg-surface rounded-md px-3.5 py-2.5 mb-4">
            <p className="text-error text-sm">{error}</p>
          </div>
        )}
        <button
          onClick={handleCreateAccount}
          disabled={loading}
          className="w-full h-[52px] bg-ink text-ground rounded-xl font-semibold text-base disabled:opacity-50 active:scale-[0.99] transition-transform"
        >
          {loading ? "Starting the press…" : "Start the press"}
        </button>
      </div>
    </div>
  );
};
